import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { CompanyDetails, InteractionData, LayerData, LayerItem } from '../../services/types';
import TypesContext from '../../context/TypesContext';
import { getIndustryTypeOptions, getSubIndustryOptions } from '../../utils/formOptions';
import CompanyInteractionTable from '../../components/Companies/CompanyInteractionTable';
import FiscalYear from '../../components/Filters/FiscalYear';
import InteractionType from '../../components/Interactions/InteractionType';
import AssistanceType from '../../components/Interactions/AssistanceType';
import BulkActionBtn from '../../components/Interactions/BulkActionBtn';
import NoResultsMessage from '../../components/Filters/NoResultsMessage';
import { calculateAvailableYears, calculateYearByType, formatDate } from '../../utils/helpers';
import AddInteractionButton from '../../components/Interactions/AddInteractionButton';

function filterInteractions(
  interactions: InteractionData[],
  year: string,
  interactionType: string,
  assistanceType: string
): InteractionData[] {
  return interactions.filter((interaction) => {
    const matchesYear = year
      ? new Date(interaction.interaction_date).getFullYear().toString() === year
      : true;

    const matchesInteractionType = interactionType
      ? interaction.interaction_type?.id?.toString() === interactionType
      : true;

    const matchesAssistanceType = assistanceType
      ? interaction.assistance_types?.some((a) => a.id.toString() === assistanceType)
      : true;

    return matchesYear && matchesInteractionType && matchesAssistanceType;
  });
};

function MapCompanyLandingPage() {
    const { typesData } = useContext(TypesContext);
    const [searchParams] = useSearchParams();
    const layerId = searchParams.get('layerId');
    const pointId = searchParams.get('id');

    const navigate = useNavigate();

    const [company, setCompany] = useState<CompanyDetails | null>(null);
    const [layerItem, setLayerItem] = useState<LayerItem | null>(null);
    const [interactions, setInteractions] = useState<InteractionData[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [year, setYear] = useState<string>('');
    const [interactionType, setInteractionType] = useState<string>('');
    const [assistanceType, setAssistanceType] = useState<string>('');
    const [industryTypeOptions, setIndustryTypeOptions] = useState<{ value: string; label: string }[]>([]);
    const [subIndustryOptions, setSubIndustryOptions] = useState<{ value: string; label: string }[]>([]);
    const [error, setError] = useState<string | null>(null);

    const filteredInteractions = filterInteractions(interactions, year, interactionType, assistanceType);

    const currFisYear = calculateYearByType(year);
    const availableYears = calculateAvailableYears();

    const handleEdit = () => {
        if (company?.id) {
        navigate(`/companies/edit/${company.id}`);
        }
    };

    const handleRemoveFilters = () => {
        setYear(currFisYear.toString());
        // setSortType('');
        setInteractionType('');
        setAssistanceType('');
    };

    useEffect(() => {
        const fetchLayerDataAndCompany = async () => {
            if (!layerId || !pointId) return;
            // setLoading(true);

            try {
                // Step 1: Fetch layer items
                const response = await fetch(
                    `${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getlayeritems?layerId=${layerId}`,
                    {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json' },
                    }
                );

                if (!response.ok) throw new Error('Failed to fetch layer items');

                const layerData: LayerData = await response.json();
                const layerItems = layerData.items;

                // Step 2: Find the item by internal `id`
                const matchedItem = layerItems.find((item) => item.id.toString() === pointId);
                if (!matchedItem) {
                    throw new Error(`No matching item found in layer for pointId: ${pointId}`);
                }

                setLayerItem(matchedItem);

                // Step 3: Use its external_id to fetch full company details
                const externalCompanyId = matchedItem.external_id;
                const companyResponse = await fetch(
                    `${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getcompanydetails?companyId=${externalCompanyId}`
                );
                const companyData = await companyResponse.json();

                if (!companyResponse.ok) {
                    throw new Error(companyData.error || 'Failed to fetch company');
                }

                setCompany(companyData.data);
                setInteractions(companyData.data.interactions);
            } catch (err: any) {
                console.error('Error during layer/company load:', err);
                Sentry.captureException(err);
                setError('Failed to load company information.');
            } finally {
                // setLoading(false);
            }
        };

        fetchLayerDataAndCompany();
    }, [layerId, pointId]);

    console.log('interactions:', interactions);

    useEffect(() => {
        if (typesData && company?.industry?.code) {
            const industryOptions = getIndustryTypeOptions(typesData, 'industry_types', company.industry.code);
            setIndustryTypeOptions(industryOptions);

            const selectedIndustry = typesData.industry_types.find(
                (type) => type.code === company.industry?.code
            );
            const subOptions = getSubIndustryOptions(selectedIndustry);
            setSubIndustryOptions(subOptions);
        }
    }, [typesData, company]);

    return (
        <div className="p-6">
            {error && (
                <div className="text-red-500 text-sm mb-4">{error}</div>
            )}

            {company && (
                <div className="mb-6 bg-white rounded-md shadow-lg border border-gray-100 overflow-hidden">
                    {/* Header */}
                    <div className="p-6 bg-[#4d4d4d] rounded-t-xl flex justify-between items-center">
                        <p className="text-xl font-bold text-white capitalize">
                            {company.legal_name || company.trade_name || 'Unnamed Company'}
                        </p>
                        <button
                            className="px-4 py-2 text-sm text-white font-bold w-36 bg-button-blue rounded-md focus:border-blue-500 hover:border-color-cai-blue hover:bg-color-cai-blue"
                            onClick={handleEdit}
                        >
                            Edit Company
                        </button>
                    </div>

                    {/* Details */}
                    <div className="p-6 space-y-2 text-gray-700 text-sm">
                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">UBI:</span>
                            <span>{company.ubi || 'N/A'}</span>
                        </div>

                        {company.industry?.code && (
                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">Industry:</span>
                            <span>{industryTypeOptions.find(opt => opt.value === company.industry?.code)?.label || company.industry?.code}</span>
                        </div>
                        )}

                        {company.sub_industry?.code && (
                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">Sub-Industry:</span>
                            <span>{subIndustryOptions.find(opt => opt.value === company.sub_industry?.code)?.label || company.sub_industry?.code}</span>
                        </div>
                        )}

                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">Total Interactions:</span>
                            <span>{company.total_interactions ?? '0'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">Last Interaction Date:</span>
                            <span>{formatDate(company.last_interaction_date)}</span>
                        </div>
                    </div>

                    {/* Interactions */}
                    <div className="my-8 rounded-b-md w-full">
                        <div className="flex flex-wrap items-center my-3 px-2">
                            <h2 className="text-gray-700 text-sm font-semibold ml-4 w-36">Recent Interactions</h2>
                            <div className="ml-2 flex flex-wrap items-center space-y-2">
                                <FiscalYear filterByYear={setYear} availableYears={availableYears} currYear={year} clearable={true} />
                                <InteractionType filterByInteractionType={setInteractionType} currInteractionType={interactionType} />
                                <AssistanceType filterByAssistanceType={setAssistanceType} currAssistanceType={assistanceType} />
                                <AddInteractionButton id={layerItem?.external_id ?? undefined} company={company} />
                                <BulkActionBtn disabled={selectedRows.length === 0} handleBulkAction={() => console.log("Bulk action:", selectedRows)} selectedRows={selectedRows} hideOption="move" />
                            </div>
                        </div>

                        {filteredInteractions.length > 0 ? (
                            <CompanyInteractionTable data={filteredInteractions} handleSelectRows={setSelectedRows} />
                        ) : (
                            <NoResultsMessage
                                itemName="interactions"
                                hasFilters={true}
                                onRemoveFilters={handleRemoveFilters}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default MapCompanyLandingPage;
